import { useMemo, useCallback } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { UseFormReturn, useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import {
  useWbsClientLabelSearchQuery,
  useWbsProjectLabelSearchQuery,
  useWbsTaskLabelSearchQuery,
  LabelValueSource,
  WbsClientLabelSearchQueryVariables,
  WbsProjectLabelSearchQueryVariables,
  WbsTaskLabelSearchQueryVariables,
  WbsClientLabelSearchQuery,
  WbsProjectLabelSearchQuery,
  WbsTaskLabelSearchQuery,
  WbsLabelValueFilterInput,
  WbsLabelValueSearchResult,
  useWbsProgramLabelSearchQuery,
  WbsProgramLabelSearchQueryVariables,
  WbsProgramLabelSearchQuery,
  LabelValueAcceptedBy
} from 'graphql/types';
import { useNoteContext } from 'modules/notes/NoteContext';
import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import { DependantLabels } from '../components/schema';
import { CPT_LABEL_PAGE_COUNT } from '../components/constants';

export const autoUpdateClient = async ({
  dependentLabels,
  selectedDate,
  refetch,
  filter,
  setValue,
  formField
}: {
  selectedDate: DateTime;
  dependentLabels: DependantLabels;
  refetch: (
    variables?: WbsClientLabelSearchQueryVariables
  ) => Promise<ApolloQueryResult<WbsClientLabelSearchQuery>>;
  filter: WbsLabelValueFilterInput | null | undefined;
  setValue: UseFormReturn['setValue'];
  formField: string;
}): Promise<WbsLabelValueSearchResult | null | undefined> => {
  const clientsResult = await refetch({
    date: selectedDate.toISODate(),
    input: {
      count: CPT_LABEL_PAGE_COUNT,
      query: undefined,
      filter: filter
    }
  });

  const clients =
    clientsResult?.data?.wbsLabelValueSearch?.clients?.items || [];
  if (clients.length === 1) {
    setValue(
      `${formField}.${dependentLabels?.client?.index}.label`,
      {
        ...dependentLabels?.client?.label,
        value: {
          idValue: clients[0].id,
          stringValue: clients[0].displayText
        },
        valueSource: LabelValueSource.User,
        acceptedBy: LabelValueAcceptedBy.User
      },
      { shouldValidate: true, shouldDirty: true }
    );
    return clients[0];
  }

  return null;
};

export const autoUpdateProgram = async ({
  dependentLabels,
  selectedDate,
  refetch,
  filter,
  setValue,
  formField
}: {
  selectedDate: DateTime;
  dependentLabels: DependantLabels;
  refetch: (
    variables?: WbsProgramLabelSearchQueryVariables
  ) => Promise<ApolloQueryResult<WbsProgramLabelSearchQuery>>;
  filter: WbsLabelValueFilterInput | null | undefined;
  setValue: UseFormReturn['setValue'];
  formField: string;
}): Promise<WbsLabelValueSearchResult | null | undefined> => {
  const programsResult = await refetch({
    date: selectedDate.toISODate(),
    input: {
      count: CPT_LABEL_PAGE_COUNT,
      query: undefined,
      filter: filter
    }
  });

  const programs =
    programsResult?.data?.wbsLabelValueSearch?.programs?.items || [];
  if (programs.length === 1) {
    setValue(
      `${formField}.${dependentLabels?.program?.index}.label`,
      {
        ...dependentLabels?.program?.label,
        value: {
          idValue: programs[0].id,
          stringValue: programs[0].displayText
        },
        valueSource: LabelValueSource.User,
        acceptedBy: LabelValueAcceptedBy.User
      },
      { shouldValidate: true, shouldDirty: true }
    );
    return programs[0];
  }

  return null;
};

export const autoUpdateProject = async ({
  dependentLabels,
  selectedDate,
  refetch,
  filter,
  setValue,
  formField
}: {
  selectedDate: DateTime;
  dependentLabels: DependantLabels;
  refetch: (
    variables?: WbsProjectLabelSearchQueryVariables
  ) => Promise<ApolloQueryResult<WbsProjectLabelSearchQuery>>;
  filter: WbsLabelValueFilterInput | null | undefined;
  setValue: UseFormReturn['setValue'];
  formField: string;
}): Promise<WbsLabelValueSearchResult | null | undefined> => {
  const projectsResult = await refetch({
    date: selectedDate.toISODate(),
    input: {
      count: CPT_LABEL_PAGE_COUNT,
      query: undefined,
      filter: filter
    }
  });
  const projects =
    projectsResult.data?.wbsLabelValueSearch?.projects?.items || [];
  if (projects.length === 1) {
    setValue(
      `${formField}.${dependentLabels.project.index}.label`,
      {
        ...dependentLabels.project.label,
        value: {
          idValue: projects[0].id,
          stringValue: projects[0].displayText
        },
        valueSource: LabelValueSource.User,
        acceptedBy: LabelValueAcceptedBy.User
      },
      { shouldValidate: true, shouldDirty: true }
    );

    return projects[0];
  }

  return null;
};

export const autoUpdateTask = async ({
  dependentLabels,
  selectedDate,
  refetch,
  filter,
  setValue,
  formField
}: {
  selectedDate: DateTime;
  dependentLabels: DependantLabels;
  refetch: (
    variables?: WbsTaskLabelSearchQueryVariables
  ) => Promise<ApolloQueryResult<WbsTaskLabelSearchQuery>>;
  filter: WbsLabelValueFilterInput | null | undefined;
  setValue: UseFormReturn['setValue'];
  formField: string;
}): Promise<void> => {
  const tasksResult = await refetch({
    date: selectedDate.toISODate(),
    input: {
      count: CPT_LABEL_PAGE_COUNT,
      query: undefined,
      filter: filter
    }
  });

  const tasks = tasksResult.data?.wbsLabelValueSearch?.tasks?.items || [];
  if (tasks.length === 1) {
    setValue(
      `${formField}.${dependentLabels.task.index}.label`,
      {
        ...dependentLabels.task.label,
        value: {
          idValue: tasks[0].id,
          stringValue: tasks[0].displayText
        },
        valueSource: LabelValueSource.User,
        acceptedBy: LabelValueAcceptedBy.User
      },
      { shouldValidate: true, shouldDirty: true }
    );
  }
};

export const usePCPTAutoUpdateHandlers = ({
  dependentLabels
}: {
  dependentLabels: DependantLabels;
}): {
  client: (
    filter: WbsLabelValueFilterInput | null | undefined
  ) => Promise<WbsLabelValueSearchResult | null | undefined>;
  project: (
    filter: WbsLabelValueFilterInput | null | undefined
  ) => Promise<WbsLabelValueSearchResult | null | undefined>;
  task: (filter: WbsLabelValueFilterInput | null | undefined) => void;
  program?: (
    filter: WbsLabelValueFilterInput | null | undefined
  ) => Promise<WbsLabelValueSearchResult | null | undefined>;
} => {
  const { setValue } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const { date } = useNoteContext();

  const { refetch: refetchClients } = useWbsClientLabelSearchQuery({
    skip: true
  });

  const { refetch: refetchProjects } = useWbsProjectLabelSearchQuery({
    skip: true
  });

  const { refetch: refetchTasks } = useWbsTaskLabelSearchQuery({
    skip: true
  });

  const { refetch: refetchPrograms } = useWbsProgramLabelSearchQuery({
    skip: true
  });

  const callbackInputs = useMemo(
    () => ({
      selectedDate: date,
      setValue: setValue,
      dependentLabels,
      formField: timesheetLabelsFormField
    }),
    [date, setValue, dependentLabels, timesheetLabelsFormField]
  );

  const clientAutoUpdate = useCallback(
    (filter: WbsLabelValueFilterInput | null | undefined) =>
      autoUpdateClient({
        refetch: refetchClients,
        filter: filter,
        ...callbackInputs
      }),
    [refetchClients, callbackInputs]
  );

  const projectAutoUpdate = useCallback(
    (filter: WbsLabelValueFilterInput | null | undefined) =>
      autoUpdateProject({
        refetch: refetchProjects,
        filter: filter,
        ...callbackInputs
      }),
    [refetchProjects, callbackInputs]
  );

  const taskAutoUpdate = useCallback(
    (filter: WbsLabelValueFilterInput | null | undefined) =>
      autoUpdateTask({
        refetch: refetchTasks,
        filter: filter,
        ...callbackInputs
      }),
    [refetchTasks, callbackInputs]
  );

  const programAutoUpdate = useCallback(
    (filter: WbsLabelValueFilterInput | null | undefined) =>
      autoUpdateProgram({
        refetch: refetchPrograms,
        filter: filter,
        ...callbackInputs
      }),
    [callbackInputs, refetchPrograms]
  );

  return {
    client: clientAutoUpdate,
    project: projectAutoUpdate,
    task: taskAutoUpdate,
    program: programAutoUpdate
  };
};

export default usePCPTAutoUpdateHandlers;
